import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import {
  getAttributeConditionsString,
  getCustomAttributesTypes
} from 'lib/customAttributes';
import { Badge, ContentLabel, ContentValue } from 'components/common';
import { AttributeTranslationsInfo } from 'components/common/customAttributes';
import { Icon } from 'components/common/icons';
import {
  UserAttributesEdit,
  UserAttributesEditLimited,
  UserAttributesOptions,
  UserAttributesPermissionsBadge
} from '.';

import type { UserCustomAttribute } from 'shared-types';

interface Props {
  attribute: UserCustomAttribute;
  isDisabled?: boolean;
  isLimited?: boolean;
  updateData: () => Promise<void>;
  userCustomAttributes: UserCustomAttribute[];
}

const UserAttributesAttributeInfo = ({
  attribute,
  isDisabled,
  isLimited,
  updateData,
  userCustomAttributes
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const availableAttributes = {};
  userCustomAttributes
    .filter(
      (availableAttribute) =>
        !attribute || availableAttribute.id !== attribute.id
    )
    .forEach((availableAttribute) => {
      availableAttributes[availableAttribute.key] = availableAttribute;
    });

  return (
    <div className="attribute">
      <div className="header">
        <div className="title">
          <div
            className="title-enabled"
            aria-label={
              attribute.isEnabled
                ? messages.commonEnabled
                : messages.commonDisabled
            }
            data-tooltip={
              attribute.isEnabled
                ? messages.commonEnabled
                : messages.commonDisabled
            }
          >
            <Icon
              icon="check-circle"
              style={{
                color: attribute.isEnabled
                  ? theme.successStandaloneColor
                  : 'rgba(0, 0, 0, 0.15)'
              }}
            />
          </div>
          <div className="title-content">
            {isLimited ? (
              attribute.titleTranslations?.[language] || attribute.title
            ) : (
              <div>
                {attribute.title}

                <AttributeTranslationsInfo
                  style={{ marginLeft: 10 }}
                  translations={attribute.titleTranslations}
                />
              </div>
            )}

            <div className="subtitle">
              {isLimited
                ? attribute.descriptionTranslations?.[language] ||
                  attribute.description
                : `${messages.attributesKey}: ${attribute.key}`}
            </div>
          </div>
        </div>

        {!isLimited ? (
          <div className="edit">
            <UserAttributesEdit
              attribute={attribute}
              updateData={updateData}
              userCustomAttributes={userCustomAttributes}
            />
          </div>
        ) : !isDisabled && attribute.validation?.matchListEnableEditing ? (
          <UserAttributesEditLimited
            attribute={attribute}
            updateData={updateData}
          />
        ) : null}
      </div>

      <div className="info">
        <div className="row-md">
          <div className="col-1of2">
            {!isLimited ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonDescription}
              >
                <ContentValue>
                  <div className="value-secondary">
                    {attribute.description || '-'}

                    <div>
                      <AttributeTranslationsInfo
                        style={{ marginTop: 5 }}
                        translations={attribute.descriptionTranslations}
                      />
                    </div>
                  </div>
                </ContentValue>
              </ContentLabel>
            ) : null}

            <ContentLabel
              horizontal={true}
              label={messages.userAttributesFormApplies}
            >
              <ContentValue style={{ flexDirection: 'column' }}>
                <div className="badge-container">
                  {[
                    {
                      text: messages.commonUser,
                      value: attribute.appliesTo?.user
                    },
                    {
                      text: messages.usersAdmin,
                      value: attribute.appliesTo?.admin
                    },
                    {
                      text: messages.usersGuest,
                      value: attribute.appliesTo?.guest
                    }
                  ]
                    .filter((item) => item.value)
                    .map((item) => (
                      <Badge key={item.text} size="small" text={item.text} />
                    ))}
                </div>
                {attribute.conditions?.length ? (
                  <div className="subtitle">
                    {getAttributeConditionsString({
                      conditions: attribute.conditions,
                      language,
                      messages,
                      userCustomAttributesByKey: availableAttributes
                    })}
                  </div>
                ) : null}
              </ContentValue>
            </ContentLabel>

            <ContentLabel horizontal={true} label={messages.commonRequired}>
              <ContentValue>
                {attribute.isRequired?.value &&
                attribute.isRequired?.conditions?.length
                  ? `${messages.commonYes}*`
                  : attribute.isRequired?.value
                  ? messages.commonYes
                  : messages.commonNo}
              </ContentValue>
            </ContentLabel>

            <ContentLabel
              horizontal={true}
              label={messages.userAttributesShowBeforeRegistration}
            >
              <ContentValue>
                {attribute.showBeforeRegistration
                  ? messages.commonYes
                  : messages.commonNo}
              </ContentValue>
            </ContentLabel>

            <ContentLabel horizontal={true} label={messages.commonType}>
              <ContentValue style={{ flexDirection: 'column' }}>
                <div>
                  {getCustomAttributesTypes({ messages })[attribute.type]}
                </div>
                {attribute.type === 'Number' ? (
                  <div className="subtitle">
                    {`${attribute.min}-${attribute.max}`}
                  </div>
                ) : attribute.type === 'ArrayWithOptions' ||
                  attribute.type === 'StringWithOptions' ? (
                  <div className="subtitle">
                    {attribute.optionGroups?.length > 1
                      ? getMessageWithPlaceholders({
                          message: messages.commonNumberOfOptionGroups,
                          placeholders: {
                            numberOfOptionGroups: attribute.optionGroups.length
                          }
                        })
                      : getMessageWithPlaceholders({
                          message: messages.commonNumberOfOptions,
                          placeholders: {
                            numberOfOptions:
                              attribute.optionGroups?.[0]?.options.length
                          }
                        })}
                  </div>
                ) : null}
              </ContentValue>
            </ContentLabel>

            {attribute.type === 'String' ? (
              <>
                <ContentLabel
                  horizontal={true}
                  label={messages.commonValidation}
                >
                  <ContentValue>
                    {attribute.validation?.matchRegex ? (
                      <div>
                        {messages.attributesValidationMatchRegex}

                        <div className="subtitle">
                          {attribute.validation.matchRegex}
                        </div>
                      </div>
                    ) : attribute.validation?.matchList?.length ? (
                      <div>
                        {messages.attributesValidationMatchList}

                        <div className="subtitle">
                          {getMessageWithPlaceholders({
                            message: messages.commonNumberOfOptions,
                            placeholders: {
                              numberOfOptions:
                                attribute.validation.matchList.length
                            }
                          })}
                        </div>
                      </div>
                    ) : attribute.validation?.requiredPasswords?.length ? (
                      <div>{messages.authRequirePassword}</div>
                    ) : attribute.validation?.validateFunction ? (
                      <div>{messages.commonCustom}</div>
                    ) : (
                      '-'
                    )}
                  </ContentValue>
                </ContentLabel>

                <ContentLabel
                  horizontal={true}
                  label={messages.attributesValidationErrorMessage}
                >
                  <ContentValue>
                    <div className="value-secondary">
                      {attribute.validation?.errorMessage || '-'}

                      <div>
                        <AttributeTranslationsInfo
                          style={{ marginTop: 5 }}
                          translations={
                            attribute.validation?.errorMessageTranslations
                          }
                        />
                      </div>
                    </div>
                  </ContentValue>
                </ContentLabel>
              </>
            ) : null}
          </div>

          {!isLimited ? (
            <div className="col-1of2">
              <ContentLabel
                horizontal={true}
                label={messages.userAttributesFormVisible}
              >
                <ContentValue>
                  <div className="badge-container">
                    {[
                      {
                        conditions: attribute.permissions.view?.user.conditions,
                        text: messages.commonUser,
                        value: attribute.permissions.view?.user.value
                      },
                      {
                        conditions:
                          attribute.permissions.view?.admin.conditions,
                        text: messages.usersAdmin,
                        value: attribute.permissions.view?.admin.value
                      },
                      {
                        conditions:
                          attribute.permissions.view?.superadmin.conditions,
                        text: messages.usersSuperadmin,
                        value: attribute.permissions.view?.superadmin.value
                      }
                    ]
                      .filter((item) => item.value)
                      .map((item) => (
                        <UserAttributesPermissionsBadge
                          conditions={item.conditions}
                          key={item.text}
                          text={item.text}
                          userCustomAttributes={userCustomAttributes}
                        />
                      ))}
                  </div>
                </ContentValue>
              </ContentLabel>

              <ContentLabel
                horizontal={true}
                label={messages.commonEditingRights}
              >
                <ContentValue>
                  <div className="badge-container">
                    {[
                      {
                        conditions: attribute.permissions.edit?.user.conditions,
                        text: messages.commonUser,
                        value: attribute.permissions.edit?.user.value
                      },
                      {
                        conditions:
                          attribute.permissions.edit?.admin.conditions,
                        text: messages.usersAdmin,
                        value: attribute.permissions.edit?.admin.value
                      },
                      {
                        conditions:
                          attribute.permissions.edit?.superadmin.conditions,
                        text: messages.usersSuperadmin,
                        value: attribute.permissions.edit?.superadmin.value
                      }
                    ]
                      .filter((item) => item.value)
                      .map((item) => (
                        <UserAttributesPermissionsBadge
                          conditions={item.conditions}
                          key={item.text}
                          text={item.text}
                          userCustomAttributes={userCustomAttributes}
                        />
                      ))}
                  </div>
                </ContentValue>
              </ContentLabel>

              <ContentLabel
                horizontal={true}
                label={'Admin settings visible to'}
              >
                <ContentValue>
                  <div className="badge-container">
                    {[
                      {
                        conditions:
                          attribute.permissions.viewSettings?.admin.conditions,
                        text: messages.usersAdmin,
                        value: attribute.permissions.viewSettings?.admin.value
                      },
                      {
                        conditions:
                          attribute.permissions.viewSettings?.superadmin
                            .conditions,
                        text: messages.usersSuperadmin,
                        value:
                          attribute.permissions.viewSettings?.superadmin.value
                      }
                    ]
                      .filter((item) => item.value)
                      .map((item) => (
                        <UserAttributesPermissionsBadge
                          conditions={item.conditions}
                          key={item.text}
                          text={item.text}
                          userCustomAttributes={userCustomAttributes}
                        />
                      ))}
                  </div>
                </ContentValue>
              </ContentLabel>

              <ContentLabel
                horizontal={true}
                label={'Admin settings editing rights'}
              >
                <ContentValue>
                  <div className="badge-container">
                    {[
                      {
                        conditions:
                          attribute.permissions.editSettings?.admin.conditions,
                        text: messages.usersAdmin,
                        value: attribute.permissions.editSettings?.admin.value
                      },
                      {
                        conditions:
                          attribute.permissions.editSettings?.superadmin
                            .conditions,
                        text: messages.usersSuperadmin,
                        value:
                          attribute.permissions.editSettings?.superadmin.value
                      }
                    ]
                      .filter((item) => item.value)
                      .map((item) => (
                        <UserAttributesPermissionsBadge
                          conditions={item.conditions}
                          key={item.text}
                          text={item.text}
                          userCustomAttributes={userCustomAttributes}
                        />
                      ))}
                  </div>
                </ContentValue>
              </ContentLabel>
            </div>
          ) : null}
        </div>
      </div>

      {attribute.type === 'ArrayWithOptions' ||
      attribute.type === 'StringWithOptions' ? (
        <div className="options">
          <UserAttributesOptions
            attribute={attribute}
            availableAttributes={availableAttributes}
            isDisabled={isDisabled}
            isLimited={isLimited}
            updateData={updateData}
          />
        </div>
      ) : null}

      <style jsx>{`
        .attribute {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .header {
          align-items: center;
          display: flex;
          justify-content: space-between;
        }
        .title {
          align-items: center;
          display: flex;
          gap: 15px;
        }
        .title-enabled {
          font-size: 1.5rem;
        }
        .title-content {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 600;
        }
        .subtitle {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 400;
          margin-top: 2px;
        }
        .info {
          margin-top: 25px;
        }
        .options {
          margin-top: 30px;
        }
        .badge-container {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-bottom: 2px;
        }
        .value-secondary {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
        }
      `}</style>
    </div>
  );
};

export default UserAttributesAttributeInfo;
